import type { FunctionComponent } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";

import { fetchActionDetail, fetchActions, fetchBerlinWideActions, fetchOrg } from "./api.js";
import { berlinTime, dateParts } from "./format.js";
import { useLocale, useT, type Translator } from "./messages.js";
import { renderRichText } from "./richtext.js";
import { ParticipationBox } from "./Participation.js";
import type { EmbedConfig, EventFilter, PublicAction, PublicActionDetail, PublicOrg } from "./types.js";

// ─── Social platform icons ────────────────────────────────────────────────
// Same glyphs as frontend/src/lib/socialIcons.ts — separate bundle, keep both
// sides in sync. Simple Icons (simpleicons.org, CC0-1.0) for the platform
// marks, our own globe for Website, the nebenan.de house from their brand mark
// without the green tile. Minified with svgo, all on the 24-unit viewBox.
export const SOCIAL_ICON_VIEWBOX = "0 0 24 24";

export const SOCIAL_ICONS: Record<string, string> = {
  "Website":
    "M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2m6.93 6h-2.95a15.7 15.7 0 0 0-1.38-3.56A8.03 8.03 0 0 1 18.92 8M12 4.04c.83 1.2 1.48 2.53 1.9 3.96h-3.8c.42-1.43 1.07-2.76 1.9-3.96M4.26 14C4.1 13.36 4 12.69 4 12s.1-1.36.26-2h3.38c-.08.66-.14 1.32-.14 2s.06 1.34.14 2zm.82 2h2.95c.32 1.25.78 2.45 1.38 3.56A8.03 8.03 0 0 1 5.08 16m2.95-8H5.08a8.03 8.03 0 0 1 4.33-3.56A15.7 15.7 0 0 0 8.03 8M12 19.96c-.83-1.2-1.48-2.53-1.9-3.96h3.8c-.42 1.43-1.07 2.76-1.9 3.96M14.34 14H9.66c-.09-.65-.16-1.32-.16-2s.07-1.35.16-2h4.68c.09.65.16 1.32.16 2s-.07 1.35-.16 2m.25 5.56A8.03 8.03 0 0 1 18.92 16h-2.95a15.7 15.7 0 0 1-1.38 3.56M16.36 14c.08-.66.14-1.32.14-2s-.06-1.34-.14-2h3.38c.16.64.26 1.31.26 2s-.1 1.36-.26 2z",
  "WhatsApp":
    "M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52s.198-.298.298-.497c.099-.198.05-.371-.025-.52s-.669-1.612-.916-2.207c-.242-.579-.487-.5-.669-.51a13 13 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074s2.096 3.2 5.077 4.487c.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413s.248-1.289.173-1.413c-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.82 9.82 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.82 11.82 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.9 11.9 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.82 11.82 0 0 0-3.48-8.413",
  "Telegram":
    "M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm4.962 7.224c.1-.002.321.023.465.14a.5.5 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024q-.159.037-5.061 3.345-.72.495-1.302.48c-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789q.04-.324.893-.663 5.247-2.286 6.998-3.014c3.332-1.386 4.025-1.627 4.476-1.635",
  "Signal":
    "M12 0q-.934 0-1.83.139l.17 1.111a11 11 0 0 1 3.32 0l.172-1.111A12 12 0 0 0 12 0M9.152.34A12 12 0 0 0 5.77 1.742l.584.961a10.8 10.8 0 0 1 3.066-1.27zm5.696 0-.268 1.094a10.8 10.8 0 0 1 3.066 1.27l.584-.962A12 12 0 0 0 14.848.34M12 2.25a9.75 9.75 0 0 0-8.539 14.459c.074.134.1.292.064.441l-1.013 4.338 4.338-1.013a.62.62 0 0 1 .441.064A9.7 9.7 0 0 0 12 21.75c5.385 0 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25m-7.092.068a12 12 0 0 0-2.59 2.59l.909.664a11 11 0 0 1 2.345-2.345zm14.184 0-.664.909a11 11 0 0 1 2.345 2.345l.909-.664a12 12 0 0 0-2.59-2.59M1.742 5.77A12 12 0 0 0 .34 9.152l1.094.268a10.8 10.8 0 0 1 1.269-3.066zm20.516 0-.961.584a10.8 10.8 0 0 1 1.27 3.066l1.093-.268a12 12 0 0 0-1.402-3.383M.138 10.168A12 12 0 0 0 0 12q0 .934.139 1.83l1.111-.17A11 11 0 0 1 1.125 12q0-.848.125-1.66zm23.723.002-1.111.17q.125.812.125 1.66c0 .848-.042 1.12-.125 1.66l1.111.172a12.1 12.1 0 0 0 0-3.662M1.434 14.58l-1.094.268a12 12 0 0 0 .96 2.591l-.265 1.14 1.096.255.36-1.539-.188-.365a10.8 10.8 0 0 1-.87-2.35m21.133 0a10.8 10.8 0 0 1-1.27 3.067l.962.584a12 12 0 0 0 1.402-3.383zm-1.793 3.848a11 11 0 0 1-2.345 2.345l.664.909a12 12 0 0 0 2.59-2.59zm-19.959 1.1L.357 21.48a1.8 1.8 0 0 0 2.162 2.161l1.954-.455-.256-1.095-1.953.455a.675.675 0 0 1-.81-.81l.454-1.954zm16.832 1.769a10.8 10.8 0 0 1-3.066 1.27l.268 1.093a12 12 0 0 0 3.382-1.402zm-10.94.213-1.54.36.256 1.095 1.139-.266c.814.415 1.683.74 2.591.961l.268-1.094a10.8 10.8 0 0 1-2.35-.869zm3.634 1.24-.172 1.111a12.1 12.1 0 0 0 3.662 0l-.17-1.111q-.812.125-1.66.125a11 11 0 0 1-1.66-.125",
  "Instagram":
    "M7.03.084c-1.277.06-2.149.264-2.91.563a5.9 5.9 0 0 0-2.124 1.388 5.9 5.9 0 0 0-1.38 2.127C.321 4.926.12 5.8.064 7.076s-.069 1.688-.063 4.947.021 3.667.083 4.947c.061 1.277.264 2.149.563 2.911.308.789.72 1.457 1.388 2.123a5.9 5.9 0 0 0 2.129 1.38c.763.295 1.636.496 2.913.552 1.278.056 1.689.069 4.947.063s3.668-.021 4.947-.082c1.28-.06 2.147-.265 2.91-.563a5.9 5.9 0 0 0 2.123-1.388 5.9 5.9 0 0 0 1.38-2.129c.295-.763.496-1.636.551-2.912.056-1.28.07-1.69.063-4.948-.006-3.258-.02-3.667-.081-4.947-.06-1.28-.264-2.148-.564-2.911a5.9 5.9 0 0 0-1.387-2.123 5.9 5.9 0 0 0-2.128-1.38c-.764-.294-1.636-.496-2.914-.55C15.647.009 15.236-.006 11.977 0S8.31.021 7.03.084m.14 21.693c-1.17-.05-1.805-.245-2.228-.408a3.7 3.7 0 0 1-1.382-.895 3.7 3.7 0 0 1-.9-1.378c-.165-.423-.363-1.058-.417-2.228-.06-1.264-.072-1.644-.08-4.848-.006-3.204.006-3.583.061-4.848.05-1.169.246-1.805.408-2.228.216-.561.477-.96.895-1.382a3.7 3.7 0 0 1 1.379-.9c.423-.165 1.057-.361 2.227-.417 1.265-.06 1.644-.072 4.848-.08 3.203-.006 3.583.006 4.85.062 1.168.05 1.804.244 2.227.408.56.216.96.475 1.382.895s.681.817.9 1.378c.165.422.362 1.056.417 2.227.06 1.265.074 1.645.08 4.848.005 3.203-.006 3.583-.061 4.848-.051 1.17-.245 1.805-.408 2.23-.216.56-.477.96-.896 1.38a3.7 3.7 0 0 1-1.378.9c-.422.165-1.058.362-2.226.418-1.266.06-1.645.072-4.85.079s-3.582-.006-4.848-.06m9.783-16.192a1.44 1.44 0 1 0 1.437-1.442 1.44 1.44 0 0 0-1.437 1.442M5.839 12.012a6.161 6.161 0 1 0 12.323-.024 6.162 6.162 0 0 0-12.323.024M8 12.008A4 4 0 1 1 12.008 16 4 4 0 0 1 8 12.008",
  "nebenan.de":
    "M21.58 5.317 13.157.428a2.34 2.34 0 0 0-2.349 0L2.39 5.317a2.35 2.35 0 0 0-1.164 2.02v9.33c.001.83.444 1.599 1.164 2.015l3.515 2.042v-5.427l1.754 1.008c.07.044.113.12.115.204v5.301l3.028 1.765a2.36 2.36 0 0 0 2.353 0l3.029-1.765v-5.3a.24.24 0 0 1 .119-.204l1.752-1.008v5.427l1.404-.815 2.113-1.227a2.33 2.33 0 0 0 1.161-2.015v-9.33a2.34 2.34 0 0 0-1.153-2.02m-10.298 16.91-2.105-1.221V16.51a1.64 1.64 0 0 0-.813-1.417l-2.628-1.514a.818.818 0 0 0-1.226.71v3.994l-1.417-.809a.94.94 0 0 1-.463-.806V7.614l8.652 5.023zM3.324 6.386l7.958 4.631V1.774zm9.366-4.611 7.956 4.611-7.956 4.631zm8.185 15.7a.94.94 0 0 0 .468-.807V7.614l-8.653 5.023v9.59l2.098-1.221V16.51a1.64 1.64 0 0 1 .822-1.417l2.633-1.514a.818.818 0 0 1 1.224.71v3.992z",
  "Facebook":
    "M9.101 23.691v-7.98H6.627v-3.667h2.474v-1.58c0-4.085 1.848-5.978 5.858-5.978.401 0 .955.042 1.468.103a9 9 0 0 1 1.141.195v3.325a9 9 0 0 0-.653-.036 27 27 0 0 0-.733-.009c-.707 0-1.259.096-1.675.309a1.7 1.7 0 0 0-.679.622c-.258.42-.374.995-.374 1.752v1.297h3.919l-.386 2.103-.287 1.564h-3.246v8.245C19.396 23.238 24 18.179 24 12.044c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.628 3.874 10.35 9.101 11.647",
  "Linktree":
    "m13.736 5.853 4.005-4.117 2.325 2.38-4.2 4.005h5.908v3.305h-5.937l4.229 4.108-2.325 2.334-5.74-5.769-5.741 5.769-2.325-2.325 4.229-4.108H2.226V8.121h5.909l-4.2-4.004 2.324-2.381 4.005 4.117V0h3.472zm-3.472 10.306h3.472V24h-3.472z",
};

// ─── Initiative color ──────────────────────────────────────────────────────
// Mirrors the platform exactly: resolve against the --cb-init-* tokens defined
// in styles.css :host, falling back to the brand color for unknown slugs.
export function resolveColor(slug: string): string {
  return `var(--cb-${slug}, var(--cb-brand))`;
}

// ─── Item labels ──────────────────────────────────────────────────────────
const ITEM_KEYS = new Set([
  "grabber", "bags_120l", "gloves", "vest", "bucket", "water_bottle", "sturdy_shoes", "sunscreen",
]);
const itemLabel = (id: string, t: Translator) => (ITEM_KEYS.has(id) ? t(`item_${id}`) : id);

// ─── Helpers ──────────────────────────────────────────────────────────────
function calcEndTime(startIso: string, durationMinutes: number): string {
  const end = new Date(new Date(startIso).getTime() + durationMinutes * 60_000);
  return berlinTime(end);
}

function mapsHref(data: PublicActionDetail): string {
  if (data.lat && data.lng)
    return `https://www.google.com/maps/search/?api=1&query=${data.lat},${data.lng}`;
  const q = data.address || data.meeting_point;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
}

function buildShareUrl(config: EmbedConfig, orgSlug: string, id: string): string {
  const base = config.shareBase || `https://cleanups.berlin/i/${orgSlug}`;
  return `${base}#event-${id}`;
}

// ─── Page size ─────────────────────────────────────────────────────────────
// 12 = lcm(2, 3, 4, 6): the tile grid runs 2/3/4/6 columns across breakpoints,
// so a "load more" page of 12 always completes whole rows — a partial last row
// only shows at the true end, never while more events remain. Mirrors the
// platform ActionsBrowser PAGE_SIZE to keep the two surfaces in step.
export const PAGE_SIZE = 12;

// ─── Filter → API params ──────────────────────────────────────────────────
// Upcoming is always drained in full (fetchAll) so the map can show every event
// and the list slices client-side. In count mode there is no time window — the
// count only governs the initial list slice (see initialVisibleCount), not the
// query, so this never sends a `limit`.
export function upcomingWindowParams(
  filter: EventFilter,
): { upcoming: boolean; after?: string; before?: string } {
  if (filter.mode === "count") return { upcoming: true };
  const now = new Date();
  const boundary = new Date(now);
  if (filter.mode === "weeks") boundary.setTime(now.getTime() + filter.value * 7 * 24 * 60 * 60 * 1000);
  else boundary.setMonth(boundary.getMonth() + filter.value);
  return { upcoming: true, after: now.toISOString(), before: boundary.toISOString() };
}

// Past stays bounded — it is never fetchAll'd, so count mode keeps its `limit`
// (otherwise a single page would still cap at 50 but pull far more than asked).
export function pastQueryParams(
  filter: EventFilter,
): { upcoming: boolean; limit?: number; after?: string; before?: string } {
  const now = new Date();
  if (filter.mode === "count") {
    return { upcoming: false, ...(filter.value > 0 ? { limit: filter.value } : {}) };
  }
  const boundary = new Date(now);
  if (filter.mode === "weeks") boundary.setTime(now.getTime() + filter.value * 7 * 24 * 60 * 60 * 1000);
  else boundary.setMonth(boundary.getMonth() + filter.value);
  const pastBoundary = new Date(now.getTime() - (boundary.getTime() - now.getTime()));
  return { upcoming: false, after: pastBoundary.toISOString(), before: now.toISOString() };
}

// The initial list slice: an explicit count wins (the operator asked for N
// first), otherwise the standard page size.
export function initialVisibleCount(filter: EventFilter): number {
  return filter.mode === "count" && filter.value > 0 ? filter.value : PAGE_SIZE;
}

// ─── Shared data fetch ─────────────────────────────────────────────────────
interface SectionData {
  status: "loading" | "ready" | "error";
  actions: PublicAction[];
}

export interface EmbedData {
  org: PublicOrg | null;
  upcoming: SectionData;
  past: SectionData;
}

// One fetch feeding both the map and the list, keyed by (org, region). Upcoming
// is drained in full so the map shows every event; past is a single bounded page.
export function useEmbedActions(config: EmbedConfig, region?: string): EmbedData {
  const [org, setOrg] = useState<PublicOrg | null>(null);
  const [upcoming, setUpcoming] = useState<SectionData>({ status: "loading", actions: [] });
  const [past, setPast] = useState<SectionData>({ status: "loading", actions: [] });

  useEffect(() => {
    if (!config.org) { setOrg(null); return; }
    let cancelled = false;
    fetchOrg(config.apiBase, config.org)
      .then((o) => { if (!cancelled) setOrg(o); })
      .catch(() => { if (!cancelled) setOrg(null); });
    return () => { cancelled = true; };
  }, [config.org, config.apiBase]);

  useEffect(() => {
    let cancelled = false;
    setUpcoming({ status: "loading", actions: [] });
    const promise = config.org
      ? fetchActions(config.apiBase, config.org, { ...upcomingWindowParams(config.upcoming), fetchAll: true })
      : fetchBerlinWideActions(config.apiBase, { ...upcomingWindowParams(config.upcoming), region, fetchAll: true });
    promise
      .then((data) => { if (!cancelled) setUpcoming({ status: "ready", actions: data.results }); })
      .catch(() => { if (!cancelled) setUpcoming({ status: "error", actions: [] }); });
    return () => { cancelled = true; };
  }, [config.org, config.apiBase, config.upcoming, region]);

  useEffect(() => {
    if (!config.past) { setPast({ status: "ready", actions: [] }); return; }
    let cancelled = false;
    setPast({ status: "loading", actions: [] });
    const promise = config.org
      ? fetchActions(config.apiBase, config.org, pastQueryParams(config.past))
      : fetchBerlinWideActions(config.apiBase, { ...pastQueryParams(config.past), region });
    promise
      .then((data) => { if (!cancelled) setPast({ status: "ready", actions: data.results }); })
      .catch(() => { if (!cancelled) setPast({ status: "error", actions: [] }); });
    return () => { cancelled = true; };
  }, [config.org, config.apiBase, config.past, region]);

  return { org, upcoming, past };
}

// ─── ListView ─────────────────────────────────────────────────────────────
// Presentational: fetching lives in useEmbedActions upstream so the map and the
// list render the same set. The list reveals the first N (initialVisibleCount)
// and grows by PAGE_SIZE on "load more" — a client-side slice, no refetch.
export const ListView: FunctionComponent<{
  config: EmbedConfig;
  data: EmbedData;
  /** Changes when the district filter changes; collapses the slice back to the
   *  initial count (mirrors the platform's reset-on-filter). Absent = never resets. */
  resetKey?: string;
}> = ({ config, data, resetKey }) => {
  const t = useT();
  const { org, upcoming, past } = data;
  const [visible, setVisible] = useState(() => initialVisibleCount(config.upcoming));
  const [selected, setSelected] = useState<{ id: string; ids: string[] } | null>(null);
  // `map` is handled by MapView upstream, so here it only ever toggles list/tile.
  const [view, setView] = useState<"list" | "tile">(
    config.view === "list" ? "list" : config.view === "tile" || !config.org ? "tile" : "list",
  );

  // Reset on filter change only — keyed on the filter, not the data, so a
  // "load more" (which never changes resetKey) is not clobbered by a late data
  // effect. The slice() below already caps a shrunken set safely.
  useEffect(() => {
    setVisible(initialVisibleCount(config.upcoming));
  }, [resetKey, config.upcoming]);

  const color = org ? resolveColor(org.color_slug) : "var(--cb-brand)";

  const isTile = view === "tile";
  const visibleUpcoming = upcoming.actions.slice(0, visible);

  // Stepping in the Lightbox spans the whole section, not just what is
  // rendered, so "load more" does not cut the navigation short.
  function renderActions(actions: PublicAction[], navIds: string[]) {
    const open = (id: string) => setSelected({ id, ids: navIds });
    if (isTile) {
      const gridStyle = config.columns
        ? { gridTemplateColumns: `repeat(${config.columns}, 1fr)` }
        : undefined;
      return (
        <ul class="cb-grid" style={gridStyle}>
          {actions.map((a) => (
            <ActionCard action={a} key={a.id} onOpen={open} primaryOrgSlug={config.org} />
          ))}
        </ul>
      );
    }
    return (
      <ul class="cb-list">
        {actions.map((a) => (
          <ActionRow action={a} key={a.id} onOpen={open} primaryOrgSlug={config.org} />
        ))}
      </ul>
    );
  }

  return (
    <>
      <div class="cb-header">
        {org ? (
          <div class="cb-org">
            <span class="cb-org-dot" style={{ background: color }} />
            <span class="cb-org-name">{org.name}</span>
          </div>
        ) : <span />}
        <div class="cb-view-toggle" role="group" aria-label={t("viewAria")}>
          <button
            type="button"
            class={`cb-view-btn${!isTile ? " active" : ""}`}
            onClick={() => setView("list")}
            aria-pressed={!isTile}
            aria-label={t("viewList")}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <rect x="0" y="1" width="14" height="2" rx="1" fill="currentColor"/>
              <rect x="0" y="6" width="14" height="2" rx="1" fill="currentColor"/>
              <rect x="0" y="11" width="14" height="2" rx="1" fill="currentColor"/>
            </svg>
          </button>
          <button
            type="button"
            class={`cb-view-btn${isTile ? " active" : ""}`}
            onClick={() => setView("tile")}
            aria-pressed={isTile}
            aria-label={t("viewTile")}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <rect x="0" y="0" width="6" height="6" rx="1.5" fill="currentColor"/>
              <rect x="8" y="0" width="6" height="6" rx="1.5" fill="currentColor"/>
              <rect x="0" y="8" width="6" height="6" rx="1.5" fill="currentColor"/>
              <rect x="8" y="8" width="6" height="6" rx="1.5" fill="currentColor"/>
            </svg>
          </button>
        </div>
      </div>

      {config.past !== null && <div class="cb-section-head">{t("sectionUpcoming")}</div>}
      {upcoming.status === "loading" && <div class="cb-empty">{t("loading")}</div>}
      {upcoming.status === "error" && (
        <div class="cb-error">{t("loadError")}</div>
      )}
      {upcoming.status === "ready" && upcoming.actions.length === 0 && (
        <div class="cb-empty">{t("emptyUpcoming")}</div>
      )}
      {upcoming.status === "ready" && upcoming.actions.length > 0 && renderActions(visibleUpcoming, upcoming.actions.map((a) => a.id))}
      {upcoming.status === "ready" && visible < upcoming.actions.length && (
        <div class="cb-load-more-wrap">
          <button
            type="button"
            class="cb-load-more"
            onClick={() => setVisible((n) => n + PAGE_SIZE)}
          >
            {t("loadMore")}
          </button>
        </div>
      )}

      {config.past !== null && (
        <>
          <div class="cb-section-divider">{t("sectionPast")}</div>
          {past.status === "loading" && <div class="cb-empty">{t("loading")}</div>}
          {past.status === "error" && (
            <div class="cb-error">{t("loadError")}</div>
          )}
          {past.status === "ready" && past.actions.length === 0 && (
            <div class="cb-empty">{t("emptyPast")}</div>
          )}
          {past.status === "ready" && past.actions.length > 0 && renderActions(past.actions, past.actions.map((a) => a.id))}
        </>
      )}

      {selected && (
        <Lightbox
          id={selected.id}
          navIds={selected.ids}
          onNavigate={(id) => setSelected({ id, ids: selected.ids })}
          config={config}
          onClose={() => setSelected(null)}
        />
      )}
    </>
  );
};

// ─── ActionRow ────────────────────────────────────────────────────────────
const ActionRow: FunctionComponent<{
  action: PublicAction;
  onOpen: (id: string) => void;
  primaryOrgSlug?: string;
}> = ({ action, onOpen, primaryOrgSlug }) => {
  const t = useT();
  const locale = useLocale();
  const d = dateParts(action.start_at, locale);
  const clock = t("clock");
  return (
    <li class="cb-item">
      <button
        type="button"
        class={`cb-row cb-row-btn${action.status === "cancelled" ? " cb-row-cancelled" : ""}`}
        onClick={() => onOpen(action.id)}
        aria-label={t("detailsOpen", { title: action.title || action.meeting_point })}
      >
        <div class="cb-date">
          <div class="cb-date-wd">{d.weekday}</div>
          <div class="cb-date-d">{d.day}</div>
          <div class="cb-date-m">{d.month}</div>
        </div>
        <div class="cb-body">
          <h3 class="cb-title">{action.title || action.meeting_point}</h3>
          <p class="cb-meta">
            {d.time}{clock ? ` ${clock}` : ""} · {action.meeting_point}
          </p>
          {orgBadgeEl(action, primaryOrgSlug)}
          {statusChipEl(action, t)}
        </div>
        <span class="cb-chevron" aria-hidden>›</span>
      </button>
    </li>
  );
};

// ─── ActionCard (tile view) ───────────────────────────────────────────────────
const ActionCard: FunctionComponent<{
  action: PublicAction;
  onOpen: (id: string) => void;
  primaryOrgSlug?: string;
}> = ({ action, onOpen, primaryOrgSlug }) => {
  const t = useT();
  const locale = useLocale();
  const d = dateParts(action.start_at, locale);
  const clock = t("clock");
  return (
    <li class="cb-tile-item">
      <button
        type="button"
        class={`cb-tile-btn${action.status === "cancelled" ? " cb-tile-cancelled" : ""}`}
        onClick={() => onOpen(action.id)}
        aria-label={t("detailsOpen", { title: action.title || action.meeting_point })}
      >
        <div class="cb-tile-photo">
          {action.cover_photo_url
            ? <img src={action.cover_photo_url} alt="" loading="lazy" />
            : <div class="cb-tile-photo-placeholder" aria-hidden />
          }
          {statusChipEl(action, t, true)}
        </div>
        <div class="cb-tile-body">
          <p class="cb-tile-date">{d.weekday}, {d.day}. {d.month}</p>
          <h3 class="cb-title">{action.title || action.meeting_point}</h3>
          <p class="cb-meta">{d.time}{clock ? ` ${clock}` : ""} · {action.meeting_point}</p>
          {orgBadgeEl(action, primaryOrgSlug, true)}
        </div>
      </button>
    </li>
  );
};

function hasEnded(startIso: string, durationMinutes: number | null): boolean {
  const start = new Date(startIso);
  const end = durationMinutes
    ? new Date(start.getTime() + durationMinutes * 60_000)
    : new Date(start.getFullYear(), start.getMonth(), start.getDate(), 23, 59, 59);
  return end < new Date();
}

// Mirrors the platform: a published event whose time has passed reads as
// "Stattgefunden" — not only status === "completed".
function displayStatus(action: {
  status: PublicAction["status"];
  start_at: string;
  duration_minutes: number | null;
}, t: Translator): { label: string; cls: string } | null {
  if (action.status === "cancelled") return { label: t("statusCancelled"), cls: "cancelled" };
  if (
    action.status === "completed" ||
    (action.status === "published" && hasEnded(action.start_at, action.duration_minutes))
  ) {
    return { label: t("statusCompleted"), cls: "completed" };
  }
  return null;
}

// Per-row org badge. In Berlin-wide mode (no primaryOrgSlug) it always shows.
// In org-scoped mode the header already names the configured org, so a plain
// own action shows no badge; a co-organized action or a guest action (hosted
// elsewhere) still renders so the co-organizer attribution is visible (#99).
// Each org's colour dot separates the names — no "×" glyph (delete-like).
function orgBadgeEl(
  action: PublicAction,
  primaryOrgSlug: string | undefined,
  collapse = false,
) {
  const org = action.org;
  if (!org) return null;
  const co = action.co_organizers ?? [];
  const isOwnSolo = primaryOrgSlug != null && org.slug === primaryOrgSlug && co.length === 0;
  if (isOwnSolo) return null;
  // Tile view: host + "+N" counter on one non-wrapping line so tile heights
  // stay equal; full partner list lives in the Lightbox.
  if (collapse && co.length > 0) {
    return (
      <p class="cb-item-org cb-item-org-collapse">
        <span class="cb-org-entry">
          <span class="cb-org-dot" style={{ background: resolveColor(org.color_slug) }} />
          <span class="cb-org-name" style={{ color: resolveColor(org.color_slug) }}>{org.name}</span>
        </span>
        <span class="cb-org-more">+{co.length}</span>
      </p>
    );
  }
  const orgs = [org, ...co];
  return (
    <p class="cb-item-org">
      {orgs.map((o) => (
        <span class="cb-org-entry" key={o.slug}>
          <span class="cb-org-dot" style={{ background: resolveColor(o.color_slug) }} />
          <span class="cb-org-name" style={{ color: resolveColor(o.color_slug) }}>{o.name}</span>
        </span>
      ))}
    </p>
  );
}

function statusChipEl(action: PublicAction, t: Translator, tile = false) {
  const s = displayStatus(action, t);
  if (!s) return null;
  return <span class={`cb-status ${s.cls}${tile ? " cb-tile-status" : ""}`}>{s.label}</span>;
}

type Stat = { id: string; label: string; value: string };
const EFFORT_STATS = new Set(["participants", "children", "hours"]);

// Mirrors collectStats in the frontend Lightbox, including the derived
// volunteer hours (participants × planned duration). The label lives under
// `stat_<id>`; "singular|plural" there picks the singular for a count of 1.
export function collectReportStats(
  data: Pick<PublicActionDetail, "report" | "duration_minutes">,
  t: Translator,
  locale: string,
): Stat[] {
  const report = data.report;
  if (!report) return [];
  const out: Stat[] = [];
  const add = (id: string, n: number | null) => {
    if (n == null) return;
    const [one, many = one] = t(`stat_${id}`).split("|");
    out.push({ id, label: n === 1 ? one : many, value: n.toLocaleString(locale) });
  };
  const people = report.participants;
  add("hours", (people && data.duration_minutes && Math.round((people * data.duration_minutes) / 60)) || null);
  add("participants", people);
  add("children", report.children);
  add("bags", report.bags_120l);
  add("kg", report.kilograms ? Number(report.kilograms) : null);
  add("cigarettes", report.cigarette_butts);
  add("bottleCaps", report.bottle_caps);
  add("cups", report.single_use_cups);
  add("bottles", report.bottles);
  add("treePits", report.tree_pits_cleaned);
  add("bulkyWaste", report.bulky_waste_cases);
  return out;
}

// The typed address only served to find the pin and goes stale when the pin
// moves, so with a pin the linked meeting point is the location (#198).
export function visibleAddress(data: Pick<PublicActionDetail, "address" | "lat" | "lng">): string | null {
  if (!data.address || data.address.startsWith("http")) return null;
  if (data.lat != null && data.lng != null) return null;
  return data.address;
}

// ─── Lightbox ─────────────────────────────────────────────────────────────
type DetailState =
  | { status: "loading" }
  | { status: "ok"; data: PublicActionDetail }
  | { status: "error" };

export const Lightbox: FunctionComponent<{
  id: string;
  config: EmbedConfig;
  onClose: () => void;
  navIds?: string[];
  onNavigate?: (id: string) => void;
}> = ({ id, config, onClose, navIds, onNavigate }) => {
  const t = useT();
  const [state, setState] = useState<DetailState>({ status: "loading" });
  const overlayRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Step through the list the action was opened from, like the platform
  // Lightbox. Arrow keys stay with the photo gallery, so this is buttons only.
  const ids = navIds ?? [];
  const index = ids.indexOf(id);
  const showNav = Boolean(onNavigate) && index !== -1 && ids.length > 1;
  const go = (delta: number) => {
    const target = ids[index + delta];
    if (target) onNavigate?.(target);
  };

  useEffect(() => {
    let alive = true;
    setState({ status: "loading" });
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    fetchActionDetail(config.apiBase, id)
      .then((data) => { if (alive) setState({ status: "ok", data }); })
      .catch(() => { if (alive) setState({ status: "error" }); });
    return () => { alive = false; };
  }, [id, config.apiBase]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      class="cb-lb-overlay"
      ref={overlayRef}
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
      role="dialog"
      aria-modal="true"
    >
      <div class="cb-lb-panel">
        <div class="cb-lb-handle" aria-hidden />
        <div class={`cb-lb-bar${showNav ? " has-nav" : ""}`}>
          {showNav && (
            <button
              type="button"
              class="cb-lb-step"
              onClick={() => go(-1)}
              disabled={index === 0}
              aria-label={t("prevAction")}
            >
              {t("prev")}
            </button>
          )}
          {showNav && <span class="cb-lb-pos">{index + 1} / {ids.length}</span>}
          <div class="cb-lb-bar-end">
            {showNav && (
              <button
                type="button"
                class="cb-lb-step"
                onClick={() => go(1)}
                disabled={index === ids.length - 1}
                aria-label={t("nextAction")}
              >
                {t("next")}
              </button>
            )}
            <button type="button" class="cb-lb-close" onClick={onClose} aria-label={t("close")}>
              ✕
            </button>
          </div>
        </div>
        <div class="cb-lb-scroll" ref={scrollRef}>
          {state.status === "loading" && <LightboxLoading />}
          {state.status === "error" && <LightboxError />}
          {state.status === "ok" && (
            <LightboxBody data={state.data} config={config} />
          )}
        </div>
        <button type="button" class="cb-lb-close-bottom" onClick={onClose}>
          {t("close")}
        </button>
      </div>
    </div>
  );
};

function LightboxLoading() {
  return (
    <div class="cb-lb-loading">
      <div class="cb-lb-pulse cb-lb-pulse-tall" />
      <div class="cb-lb-pulse cb-lb-pulse-wide" style={{ marginTop: "16px" }} />
      <div class="cb-lb-pulse cb-lb-pulse-mid" style={{ marginTop: "8px" }} />
    </div>
  );
}

function LightboxError() {
  const t = useT();
  return (
    <div class="cb-lb-body">
      <p class="cb-error">{t("detailError")}</p>
    </div>
  );
}

function LightboxBody({ data, config }: { data: PublicActionDetail; config: EmbedConfig }) {
  const t = useT();
  const locale = useLocale();
  const d = dateParts(data.start_at, locale);
  const clock = t("clock");
  const endTime = data.duration_minutes ? calcEndTime(data.start_at, data.duration_minutes) : null;
  const color = resolveColor(data.org.color_slug);
  const statusInfo = displayStatus(data, t);
  const isUpcoming = data.status === "published" && !hasEnded(data.start_at, data.duration_minutes);
  const isPast = data.status === "completed" || (data.status === "published" && !isUpcoming);

  const report = data.report;
  const reportPhotos = (report?.photos ?? []).filter((p) => !p.is_exotic);
  const exoticPhoto = report?.photos.find((p) => p.is_exotic) ?? null;
  const stats = collectReportStats(data, t, locale);

  // Upcoming actions lead with the cover, past ones with what happened (#208).
  const cover: GalleryPhoto[] = data.cover_photo_url ? [{ image_url: data.cover_photo_url }] : [];
  const found: GalleryPhoto[] = exoticPhoto ? [{ image_url: exoticPhoto.image_url, exotic: true }] : [];
  const photos: GalleryPhoto[] = isPast ? [...reportPhotos, ...found, ...cover] : [...cover, ...reportPhotos, ...found];
  const [photoIndex, setPhotoIndex] = useState(0);
  const articleRef = useRef<HTMLElement>(null);
  const showExotic = () => {
    setPhotoIndex(photos.findIndex((p) => p.exotic));
    articleRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const tr = data.translation;
  const logisticsRows: Row[] = [];
  if (data.provided.length > 0)
    logisticsRows.push({ icon: "🧤", label: t("provided"), value: data.provided.map((i) => itemLabel(i, t)).join(", ") });
  if (data.bring_along.length > 0)
    logisticsRows.push({ icon: "🎒", label: t("bringAlong"), value: data.bring_along.map((i) => itemLabel(i, t)).join(", ") });
  if (tr.weather_policy)
    logisticsRows.push({ icon: "🌦️", label: t("weather"), value: tr.weather_policy });

  const addressText = visibleAddress(data);
  const location = (addressText || data.meeting_point) && (
    <dl class="cb-lb-location">
      {addressText && (
        <div class="cb-lb-loc-row">
          <dt>{t("addr")}</dt>
          <dd>{addressText}</dd>
        </div>
      )}
      {data.meeting_point && (
        <div class="cb-lb-loc-row">
          <dt>{t("meetingPoint")}</dt>
          <dd class="cb-lb-loc-strong">
            {data.lat != null && data.lng != null
              ? <a href={mapsHref(data)} target="_blank" rel="noopener noreferrer">{data.meeting_point}</a>
              : data.meeting_point}
          </dd>
        </div>
      )}
    </dl>
  );

  const aboutRows = [
    [t("whatToExpect"), tr.what_to_expect],
    [t("accessibility"), tr.accessibility_notes],
    [t("questions"), tr.contact_questions],
    [t("wasteDisposal"), tr.waste_disposal_note],
  ].filter((r): r is [string, string] => Boolean(r[1]));
  const about = aboutRows.map(([label, text]) => (
    <div key={label} class="cb-lb-detail-row">
      <p class="cb-lb-detail-label">{label}</p>
      <p>{text}</p>
    </div>
  ));
  const intro = tr.intro_text && (
    <div class="cb-lb-intro cb-richtext">{renderRichText(tr.intro_text)}</div>
  );

  const socialLinks = [
    { url: data.org.website_url, label: "Website" },
    { url: data.org.whatsapp_url, label: "WhatsApp" },
    { url: data.org.signal_url, label: "Signal" },
    { url: data.org.telegram_url, label: "Telegram" },
    { url: data.org.instagram_url, label: "Instagram" },
    { url: data.org.nebenan_url, label: "nebenan.de" },
    { url: data.org.facebook_url, label: "Facebook" },
    { url: data.org.linktree_url, label: "Linktree" },
  ].filter((l) => Boolean(l.url));

  return (
    <article ref={articleRef}>
      <Gallery
        photos={photos}
        index={photoIndex}
        onIndex={setPhotoIndex}
        caption={report?.exotic_find_text ?? ""}
      />
      <div class="cb-lb-body">
        <div class="cb-lb-head">
          <div class="cb-lb-head-main">
            {statusInfo && (
              <span class={`cb-status ${statusInfo.cls}`} style={{ marginBottom: "8px" }}>
                {statusInfo.label}
              </span>
            )}
            <h2 class="cb-lb-title">{data.title || data.meeting_point || t("titleFallback")}</h2>
          </div>
          <ShareButton url={buildShareUrl(config, data.org.slug, data.id)} />
        </div>

        {/* Org line — full attribution for co-organized actions (#99) */}
        {(() => {
          const co = data.co_organizers ?? [];
          const hostLink = (
            <a
              href={`https://cleanups.berlin/i/${data.org.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              class="cb-lb-org"
              style={{ color }}
            >
              <span class="cb-lb-org-dot" style={{ background: color }} aria-hidden />
              {data.org.name}
            </a>
          );
          if (co.length === 0) return hostLink;
          return (
            <p class="cb-lb-org-line">
              {t("hostedBy")} {hostLink} {t("jointlyWith")}{" "}
              {co.map((c, i) => (
                <span key={c.slug}>
                  <a
                    href={`https://cleanups.berlin/i/${c.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    class="cb-lb-org"
                    style={{ color: resolveColor(c.color_slug) }}
                  >
                    <span class="cb-lb-org-dot" style={{ background: resolveColor(c.color_slug) }} aria-hidden />
                    {c.name}
                  </a>
                  {i < co.length - 1 ? ", " : ""}
                </span>
              ))}
            </p>
          );
        })()}

        {/* Date; the calendar link only helps before the date */}
        <p class="cb-lb-meta">
          📅 {d.weekday}, {d.day}. {d.month} · {d.time}{endTime ? `–${endTime}` : ""}{clock ? ` ${clock}` : ""}
          {isUpcoming && (
            <>
              {" · "}
              <a
                href={`${config.apiBase}/api/actions/${data.id}/event.ics`}
                download="event.ics"
                title={t("icsTitle", { org: data.org.name })}
                class="cb-lb-link"
              >
                {t("calendar")}
              </a>
            </>
          )}
        </p>

        {isPast ? (
          // After the date the result matters (#198): report first, the
          // announcement folded away at the end.
          <>
            <section class="cb-lb-card">
              <h3 class="cb-lb-h3">{t("report")}</h3>
              {[
                { title: t("statGroupEffort"), items: stats.filter((s) => EFFORT_STATS.has(s.id)) },
                { title: t("statGroupCleanedUp"), items: stats.filter((s) => !EFFORT_STATS.has(s.id)) },
              ].filter((g) => g.items.length > 0).map((g) => (
                <div key={g.title}>
                  <h4 class="cb-lb-detail-label cb-lb-stat-group">{g.title}</h4>
                  <dl class="cb-lb-stats">
                    {g.items.map((s) => (
                      <div key={s.id} class="cb-lb-stat"><dt>{s.label}</dt><dd>{s.value}</dd></div>
                    ))}
                  </dl>
                </div>
              ))}
              {report?.narrative_text ? (
                <div class="cb-lb-report-text cb-richtext">{renderRichText(report.narrative_text)}</div>
              ) : (
                <p class="cb-lb-report-empty">{t("noReport")}</p>
              )}
            </section>

            {(report?.exotic_find_text || exoticPhoto) && (
              <section class="cb-lb-card">
                <p class="cb-lb-detail-label">{t("exoticFind")}</p>
                <div class="cb-lb-find">
                  {exoticPhoto && (
                    <button type="button" class="cb-lb-find-photo" onClick={showExotic} aria-label={t("showPhoto")}>
                      <img src={exoticPhoto.image_url} alt="" loading="lazy" />
                    </button>
                  )}
                  {report?.exotic_find_text && <p class="cb-lb-report-text">{report.exotic_find_text}</p>}
                </div>
              </section>
            )}

            {(intro || location || logisticsRows.length > 0 || about.length > 0) && (
              <details class="cb-lb-details">
                <summary><h3 class="cb-lb-h3">{t("announcement")}</h3><Chevron /></summary>
                <div class="cb-lb-details-body">
                  {intro}
                  {location}
                  {logisticsRows.length > 0 && <RowList rows={logisticsRows} />}
                  {about.length > 0 && <div class="cb-lb-about">{about}</div>}
                </div>
              </details>
            )}
          </>
        ) : (
          <>
            {intro}
            {location}

            {/* Participation (M7) — upcoming published events only, if org enables it */}
            {data.org.participation_enabled && isUpcoming && (
              <ParticipationBox key={data.id} data={data} apiBase={config.apiBase} />
            )}

            {data.status === "cancelled" && data.cancelled_reason && (
              <div class="cb-lb-cancelled">{data.cancelled_reason}</div>
            )}

            {logisticsRows.length > 0 && (
              <div class="cb-lb-logistics"><RowList rows={logisticsRows} /></div>
            )}

            {about.length > 0 && (
              <details class="cb-lb-details" open={isUpcoming}>
                <summary><span>{t("about")}</span><Chevron /></summary>
                <div class="cb-lb-details-body">{about}</div>
              </details>
            )}
          </>
        )}

        {/* Per-event contact (mirrors the platform Lightbox) */}
        {(data.contact_name || data.contact_phone || data.contact_email) && (
          <div class="cb-lb-contact">
            <p class="cb-lb-social-label">{t("contactForEvent")}</p>
            {data.contact_name && <p class="cb-lb-contact-name">{data.contact_name}</p>}
            <div class="cb-lb-contact-links">
              {data.contact_phone && (
                <a class="cb-lb-btn cb-lb-btn-sm" href={`tel:${data.contact_phone.replace(/\s/g, "")}`}>
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
                    <path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1-9.4 0-17-7.6-17-17 0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1z" />
                  </svg>
                  {t("call")}
                </a>
              )}
              {data.contact_email && (
                <a class="cb-lb-btn cb-lb-btn-sm" href={`mailto:${data.contact_email}`}>
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
                    <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4-8 5-8-5V6l8 5 8-5v2z" />
                  </svg>
                  {t("email")}
                </a>
              )}
            </div>
          </div>
        )}

        {/* Social links */}
        {socialLinks.length > 0 && (
          <div class="cb-lb-social">
            <p class="cb-lb-social-label">{t("followInitiative")}</p>
            <div class="cb-lb-social-links">
              {socialLinks.map((l) => (
                <a
                  key={l.label}
                  href={l.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={l.label}
                  title={l.url}
                  class="cb-lb-social-icon"
                >
                  <svg viewBox={SOCIAL_ICON_VIEWBOX} width="18" height="18" fill="currentColor" aria-hidden="true">
                    <path d={SOCIAL_ICONS[l.label] ?? ""} />
                  </svg>
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </article>
  );
}

type Row = { icon: string; label: string; value: string };

function RowList({ rows }: { rows: Row[] }) {
  return (
    <dl class="cb-lb-log-list">
      {rows.map((r) => (
        <div key={r.label} class="cb-lb-log-row">
          <dt><span aria-hidden>{r.icon}</span><span>{r.label}</span></dt>
          <dd>{r.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function Chevron() {
  return (
    <svg class="cb-lb-chevron" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

// Icon only, next to the title; a check replaces it while the link sits in
// the clipboard, the status text is for screen readers.
function ShareButton({ url }: { url: string }) {
  const t = useT();
  const [copied, setCopied] = useState(false);
  const onClick = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard blocked */ }
  };
  return (
    <span class="cb-lb-share">
      <button type="button" class="cb-lb-share-btn" onClick={onClick} aria-label={t("share")} title={t("share")}>
        {copied ? <span aria-hidden>✓</span> : (
          <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
            <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z" />
          </svg>
        )}
      </button>
      <span class="cb-sr-only" role="status" aria-live="polite">{copied ? t("linkCopied") : ""}</span>
    </span>
  );
}

type GalleryPhoto = { image_url: string; exotic?: boolean };

// Hero with arrows, swipe and ←/→, thumbnails right under it (#208). The
// curious find carries a ✨ badge and its sentence as caption.
function Gallery({ photos, index, onIndex, caption }: {
  photos: GalleryPhoto[];
  index: number;
  onIndex: (i: number) => void;
  caption: string;
}) {
  const t = useT();
  const n = photos.length;
  const touchX = useRef<number | null>(null);
  const go = (i: number) => onIndex(Math.max(0, Math.min(n - 1, i)));

  useEffect(() => {
    if (n < 2) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") go(index - 1);
      if (e.key === "ArrowRight") go(index + 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  if (n === 0) return null;
  const current = photos[Math.min(index, n - 1)];
  return (
    <>
      <div
        class="cb-lb-hero"
        onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
        onTouchEnd={(e) => {
          if (touchX.current === null) return;
          const dx = e.changedTouches[0].clientX - touchX.current;
          touchX.current = null;
          if (Math.abs(dx) >= 40) go(index + (dx > 0 ? -1 : 1));
        }}
      >
        <img src={current.image_url} alt="" loading="eager" />
        {index > 0 && (
          <button type="button" class="cb-lb-nav cb-lb-prev" onClick={() => go(index - 1)} aria-label={t("prevPhoto")}>‹</button>
        )}
        {index < n - 1 && (
          <button type="button" class="cb-lb-nav cb-lb-next" onClick={() => go(index + 1)} aria-label={t("nextPhoto")}>›</button>
        )}
        {n > 1 && <span class="cb-lb-count">{index + 1} / {n}</span>}
      </div>
      {current.exotic && caption && (
        <p class="cb-lb-caption"><span class="cb-lb-detail-label">{t("exoticFind")}</span> {caption}</p>
      )}
      {n > 1 && (
        <ul class="cb-lb-strip" aria-label={t("photos")}>
          {photos.map((p, i) => (
            <li key={p.image_url}>
              <button
                type="button"
                class={i === index ? "active" : ""}
                onClick={() => onIndex(i)}
                aria-label={p.exotic ? `${t("showPhoto")}: ${t("exoticFind")}` : t("showPhoto")}
                aria-pressed={i === index}
              >
                <img src={p.image_url} alt="" loading="lazy" />
                {p.exotic && <span class="cb-lb-badge" aria-hidden>✨</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
